/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQueryClient } from 'react-query'
import { KTIcon } from '../../../../helpers'
import { Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table'
import moment from 'moment'
import Swal from 'sweetalert2'
import { axiosClientAuth } from '../../../../../app/utils'
import clsx from 'clsx'
import { DrawerURLRedirection } from '../drawers/DrawerURLRedirection'
import { DatePicker } from 'antd'
import { useAuth } from '../../../../../app/modules/auth'
import RejectionReasonModal from '../modal/RejectionReasonModal'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useLayout } from '../../../../layout/core'
import { URLS_QUERY_KEY } from '../../../../../app/utils'

interface urlDataType {
  name: string
  url: string
  created_at: string
  status: boolean
  uuid: string
  is_trusted: boolean
  pe_name: string
}

type Props = {
  className: string
  urlData: urlDataType[]
  loading?: boolean
  filter?: boolean
  title?: string
  subtitle?: string
  onRefetch?: () => void
  serverPage?: number
  serverPageSize?: number
  serverTotalPages?: number
  serverTotalCount?: number
  onServerPageChange?: (page: number) => void
  onServerPageSizeChange?: (pageSize: number) => void
  onServerSearchChange?: (search: string) => void
  onServerDateChange?: (from: string, to: string) => void
  onServerTypeChange?: (type: string) => void
  serverType?: string
  onServerStatusChange?: (status: string) => void
  serverStatus?: string
}

type columnType = {
  [x: string]: any
  name: string
  url: string
  created_at: string
  status: boolean
  is_trusted: boolean
  pe_name: string
}

const TablesWidgetURLScrubbingLogs: React.FC<Props> = ({
  className,
  urlData,
  loading = false,
  filter = true,
  onRefetch,
  title,
  subtitle,
  serverPage = 1,
  serverPageSize = 10,
  serverTotalPages = 1,
  serverTotalCount,
  onServerPageChange,
  onServerPageSizeChange,
  onServerSearchChange,
  onServerDateChange,
  onServerTypeChange,
  serverType = '',
  onServerStatusChange,
  serverStatus = '',
}) => {
  const intl = useIntl()
  const resolvedTitle = title ?? intl.formatMessage({ id: 'URL.HEADING', defaultMessage: 'URLs Approvals' })
  const resolvedSubtitle = subtitle ?? intl.formatMessage({ id: 'URL.SUBTITLE', defaultMessage: 'Total URLs : ' })
  const { checkPermission } = useAuth()
  const [mData, setMData] = useState(urlData)
  const [searchValue, setSearchValue] = useState('')
  const { setUuidOfURLApproval, setUrlApprovalItem, showLoader, hideLoader } = useLayout()
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionUuid, setRejectionUuid] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const statusMutation = useMutation(
    ({ uuid, active, reason }: { uuid: string; active: boolean; reason?: string }) =>
      axiosClientAuth.patch(`/urls/${uuid}/status/`, { active, reason: reason || '' }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(URLS_QUERY_KEY)
        queryClient.invalidateQueries(['analytics-resources'])
      },
      onError: (err: any) => {
        Swal.fire({
          title: 'Error',
          text: err?.response?.data?.message || 'An error occurred',
          icon: 'error',
        })
      },
    }
  )

  useEffect(() => {
    setMData(urlData)
  }, [urlData])

  const updateUrl = (uuid: string, status: boolean, text: string) => {
    if (status === true) {
      setRejectionUuid(uuid)
      setShowRejectModal(true)
    }
  }

  const onClickBuyTrusted = async (data: any, status) => {
    try {
      // const res = await axiosClientAuth.post('/payments/init-payment', {
      //   paid_for: 'convert_to_verified_url',
      //   url_uuid: data.uuid,
      // })
      // if (res.data.status) {
      //   setIframe(res.data.data.iframe_url)
      //   setPaymentLayoutHeading('URL Registration')
      //   setPaymentLayoutDescription(data.url)
      //   navigate('/payment')
      // }
      Swal.fire({
        title: 'Are You Sure ?',
        icon: 'warning',
        text: `You want to convert this url to ${status ? "trusted" : "not trusted"} ?`,
        showCancelButton: true,
      }).then(async (resp) => {
        if (resp?.isConfirmed) {
          const res = await axiosClientAuth.patch('/main/urls', {
            is_trusted: status,
            uuid: data.uuid,
          });
          if (res.data.status) {
            // setPaymentLayoutHeading('URL Registration')
            // setPaymentLayoutDescription(data.url)
            // navigate('/payment')
            onRefetch?.();
          }
        }
      })
    } catch (err: any) {
      Swal.fire({ title: 'Error', text: err.response.data.message || "Error Occured", icon: 'error' })
    }
  }

  const handleRejectionSubmit = async (reason: string) => {
    if (!rejectionUuid) return
    showLoader()
    try {
      await statusMutation.mutateAsync({ uuid: rejectionUuid, active: false, reason })
      Swal.fire({
        titleText: 'Success!',
        text: 'URL Deactivated Successfully',
        icon: 'success',
        confirmButtonText: 'Ok',
      })
    } catch (err) {
      console.log(err)
    } finally {
      hideLoader()
      setRejectionUuid(null)
      setShowRejectModal(false)
    }
  }

  const handleActivate = async (uuid: string) => {
    const resp = await Swal.fire({
      title: 'Are You Sure?',
      icon: 'warning',
      text: 'Activate this URL?',
      showCancelButton: true,
    })
    if (!resp.isConfirmed) return
    showLoader()
    try {
      await statusMutation.mutateAsync({ uuid, active: true })
      Swal.fire({
        titleText: 'Success!',
        text: 'URL Activated Successfully',
        icon: 'success',
        confirmButtonText: 'Ok',
      })
    } catch (err) {
      console.log(err)
    } finally {
      hideLoader()
    }
  }


  const columns: ReadonlyArray<Column<columnType>> = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'TABLE.SL_NO', defaultMessage: 'S.No' }),
        id: 'serial-number',
        Cell: ({ row }) => row.index + 1,
      } as unknown as Column<columnType>,
      {
        Header: intl.formatMessage({ id: 'TABLE.URL', defaultMessage: 'URL' }), accessor: 'url', Cell: ({ value, row }: any) => {
          const maxLength = 40;
          const displayUrl = value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
          return (
            <>
              <span className="url-cell" title={value}>{displayUrl}</span>
              <span className='d-block text-muted fw-semibold'>Updated on {moment(row?.original?.updated_at).format("DD MMM YYYY")}</span>
            </>
          )
        },
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.PE_DETAILS', defaultMessage: 'PE Details' }),
        accessor: 'pe_id',
        Cell: ({ ...props }) => {
          return (
            <>
              <span>{props.row.original.pe_name}</span>
              <span className='d-block text-muted fw-semibold'>{props.value}</span>
            </>
          )
        },
      },
      {
        Header: 'pe_name', accessor: 'pe_name', show: false, visible: false,
        style: { display: 'none' }
      },
      // { Header: 'URL NAME', accessor: 'name' },
      {
        Header: intl.formatMessage({ id: 'TABLE.TRUSTED_URL', defaultMessage: 'Trusted URL' }),
        accessor: 'is_trusted',
        Cell: ({ ...props }) => (
          <div className='d-flex'>
            {props.value === true ? (
              <span className='badge badge-light-success'>{intl.formatMessage({ id: 'COMMON.TRUSTED', defaultMessage: 'Trusted' })}</span>
            ) : (
              <span className='badge badge-light-primary'>{intl.formatMessage({ id: 'COMMON.STATIC', defaultMessage: 'Static' })}</span>
            )}
          </div>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.DATE', defaultMessage: 'DATE' }),
        accessor: 'created_at',
        Cell: ({ ...props }) => {
          return (
            <>
              <span>{moment(props.value).format('DD/MM/YYYY')}</span>
              <span className='d-block text-muted fw-semibold'>
                {moment(props.value).format('LT')}
              </span>
            </>
          )
        },
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.STATUS', defaultMessage: 'STATUS' }),
        accessor: 'is_blacklisted_by_tcil',
        Cell: ({ ...props }) => {
          return props.value === true ? (
            <span className='badge badge-light-danger fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.BLACKLISTED', defaultMessage: 'Blacklisted' })}</span>
          ) : props.value === false ? (
            <span className='badge badge-light-success fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.WHITELISTED', defaultMessage: 'Whitelisted' })}</span>
          ) : (
            <span className='badge badge-light-warning fw-bold px-4 py-3'>{intl.formatMessage({ id: 'COMMON.PENDING', defaultMessage: 'Pending' })}</span>
          )
        },
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.ACTION', defaultMessage: 'Action' }),
        id: 'col-view',
        Cell: ({ ...props }) => {
          const uuid = props.row.original.uuid
          const status = props.row.values.is_blacklisted_by_tcil
          const isTrusted = props.row.original.is_trusted === true
          return (
            <div className='d-flex flex-wrap gap-6' style={{ width: "200px" }}>
              <button
                type="button"
                id='url-redirection-toggle'
                className='btn h-35px w-65px btn-icon btn-light-info'
                onClick={() => { setUuidOfURLApproval(uuid); setUrlApprovalItem?.(props.row.original) }}
                disabled={isTrusted}
              >
                {intl.formatMessage({ id: 'COMMON.VIEW', defaultMessage: 'View' })}
              </button>
              {checkPermission('URL Verification', 'can_edit') && (
                <>
                  {status === true && (
                    <button
                      className='btn h-35px btn-icon btn btn-light-success'
                      title='Activate URL'
                      onClick={() => handleActivate(uuid)}
                    >
                      <KTIcon iconName='check' className='text-hover-white fs-1 text-success' />
                    </button>
                  )}
                  {status !== true && (
                    <button
                      className='btn h-35px btn-icon btn btn-light-danger'
                      title='Deactivate URL'
                      onClick={() => updateUrl(uuid, true, 'Deactivated')}
                    >
                      <KTIcon iconName='cross' className='text-hover-white fs-1 text-danger' />
                    </button>
                  )}
                </>
              )}
            </div>
          )
        },
      },
    ],
    [urlData, intl]
  )

  const scrubbingLogsTable = useTable(
    {
      columns,
      data: mData,
      initialState: {
        pageSize: serverPageSize,
        hiddenColumns: columns.map((column: any) => {
          if (column.show === false) return column.accessor || column.id;
        }),
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  useEffect(() => {
    scrubbingLogsTable.setPageSize(serverPageSize)
  }, [serverPageSize]) // eslint-disable-line react-hooks/exhaustive-deps
  const { RangePicker } = DatePicker

  const onDateChange = (value, datestring: any) => {
    if (onServerDateChange) {
      const [from, to] = (datestring as string[]) ?? []
      onServerDateChange(from ?? '', to ?? '')
      return
    }
    if (datestring.length == 0 || (datestring[0] == '' && datestring[1] == '')) {
      setMData(urlData)
      return;
    } else {
      const temp = urlData.filter((route) => {
        const routeDate = moment(new Date(route?.created_at)).format("DD-MM-YYYY");
        const startDate = moment(new Date(datestring[0])).format("DD-MM-YYYY");
        const endDate = moment(new Date(datestring[1])).format("DD-MM-YYYY");

        return routeDate >= startDate && routeDate <= endDate
      })
      setMData(temp)
    }
  }

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{resolvedTitle}</span>
          <span className='text-muted fw-semibold fs-7'>{resolvedSubtitle} {serverTotalCount ?? scrubbingLogsTable?.rows?.length}</span>
        </h3>
        <div className='d-flex align-items-center'>
          {
            filter ?
              <>
                <RangePicker
                  className='bg-secondary-custom text-white border-0 shadow-none'
                  onChange={onDateChange}
                  style={{ height: '34px' }}
                  disabledDate={(current) => {
                    return current && current.valueOf() > Date.now();
                  }}
                />
                {/* <span className='d-flex align-items-center mx-2'>
                  <input type='checkbox' id='trustedCheckBox' onChange={() => scrubbingLogsTable?.headers[4].setFilter(prev => prev ? !prev : true)} checked={scrubbingLogsTable?.headers[4].filterValue || false} />
                  <label className='ms-2' htmlFor="trustedCheckBox">Is Trusted</label>
                </span> */}

                <select
                  className='form-select form-select-sm form-select-solid w-auto mx-2'
                  value={serverType}
                  onChange={(e) => onServerTypeChange?.(e.target.value)}
                >
                  <option value=''>{intl.formatMessage({ id: 'COMMON.FILTER_BY_TRUSTED', defaultMessage: 'Filter By Trusted' })}</option>
                  <option value='trusted'>{intl.formatMessage({ id: 'COMMON.TRUSTED', defaultMessage: 'Trusted' })}</option>
                  <option value='normal'>{intl.formatMessage({ id: 'COMMON.NOT_TRUSTED', defaultMessage: 'Not Trusted' })}</option>
                </select>

                <select
                  className='form-select form-select-sm form-select-solid w-auto mx-2'
                  value={serverStatus}
                  onChange={(e) => onServerStatusChange?.(e.target.value)}
                >
                  <option value=''>{intl.formatMessage({ id: 'COMMON.FILTER_BY_STATUS', defaultMessage: 'Filter By Status' })}</option>
                  <option value='whitelist'>{intl.formatMessage({ id: 'COMMON.WHITELISTED', defaultMessage: 'Whitelisted' })}</option>
                  <option value='blacklist'>{intl.formatMessage({ id: 'COMMON.BLACKLISTED', defaultMessage: 'Blacklisted' })}</option>
                  <option value='pending'>{intl.formatMessage({ id: 'COMMON.PENDING', defaultMessage: 'Pending' })}</option>
                </select>

                {/* <select
                  className='form-select form-select-sm form-select-solid w-auto'
                  value={scrubbingLogsTable?.headers[7].filterValue}
                  onChange={(e) => scrubbingLogsTable?.headers[7].setFilter(e.target.value)}
                >
                  {['All', 'false', 'true'].map((item) => (
                    <option key={item} value={item == 'All' ? '' : item}>
                      {
                        item == 'All' ? 'Filter By Status'
                          : item == 'false' ? 'Active'
                            : 'Inactive'
                      }
                    </option>
                  ))}
                </select> */}
                <div className='d-flex align-items-center position-relative my-1 ms-2'>
                  <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
                  <input
                    type='text'
                    id='kt_filter_search'
                    className='form-control form-control-solid form-select-sm w-150px ps-9'
                    placeholder={intl.formatMessage({ id: 'COMMON.SEARCH_URL', defaultMessage: 'Search URL' })}
                    value={onServerSearchChange ? searchValue : scrubbingLogsTable?.state.globalFilter}
                    onChange={(e) => {
                      if (onServerSearchChange) {
                        setSearchValue(e.target.value)
                        onServerSearchChange(e.target.value)
                      } else {
                        scrubbingLogsTable?.setGlobalFilter(e.target.value)
                      }
                    }}
                  />
                </div>
              </>
              : <>
                <button
                  className='btn h-35px btn-icon btn-primary me-3'
                  style={{ width: "100px" }}
                  onClick={() => window.location.href = "/urlverification"}
                >
                  {intl.formatMessage({ id: 'COMMON.VIEW_ALL', defaultMessage: 'View All' })}
                </button>
              </>
          }
        </div>
      </div>
      {/* end::Header */}
      {/* begin::Body */}
      <div className='card-body pt-3'>
        <div className='table-responsive'>
          <table
            {...scrubbingLogsTable?.getTableProps()}
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          >
            <thead>
              {scrubbingLogsTable?.headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      className={`
                      ${column.id === 'is_trusted' ? 'w-100px' : ''}
                      ${column.id === 'col-view' ? 'w-200px' : ''}`}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <KTIcon iconName='down' className='text-gray ms-1' />
                        ) : (
                          <KTIcon iconName='up' className='text-gray ms-1' />
                        )
                      ) : (
                        ''
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...scrubbingLogsTable?.getTableBodyProps()}>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className='text-center py-6'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : (scrubbingLogsTable?.page.length > 0 &&
                scrubbingLogsTable?.page.map((row) => {
                  scrubbingLogsTable?.prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell: any) => (
                        <td className='fw-bold' {...cell.getCellProps()}>
                          <></>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  )
                })) || (
                  <tr>
                    <td colSpan={scrubbingLogsTable.visibleColumns.length} className='text-center'>
                      <span className='fw-bold'>No data available in table</span>
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
          {filter && onServerPageChange && (
            <div className='d-flex justify-content-between align-items-center mt-5'>
              <div>
                <select
                  className='form-select form-select-sm form-select-solid'
                  value={serverPageSize}
                  onChange={(e) => onServerPageSizeChange?.(parseInt(e.target.value))}
                >
                  {[10, 20, 25, 50, 100].map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <span>
                  Page{' '}
                  <strong>
                    {serverPage} of {serverTotalPages}
                  </strong>
                </span>
              </div>
              <div>
                <button
                  className='btn btn-sm'
                  onClick={() => onServerPageChange(serverPage - 1)}
                  disabled={serverPage <= 1}
                >
                  <KTIcon iconName='left' className='text-gray fs-1 me-5' />
                </button>
                <button
                  className='btn btn-sm'
                  onClick={() => onServerPageChange(serverPage + 1)}
                  disabled={serverPage >= serverTotalPages}
                >
                  <KTIcon iconName='right' className='text-gray fs-1 me-5' />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* <DrawerURLRedirection urlDrawerData={urlDrawerData} /> */}
      {showRejectModal && <RejectionReasonModal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        onSubmit={handleRejectionSubmit}
      />}
    </div>
  )
}

export { TablesWidgetURLScrubbingLogs }
